import {
    CONCURRENT_REQUESTS,
    ProgressCallback,
    BatchStartCallback,
    BatchCompleteCallback,
} from './types'

// Utility to yield to the event loop and prevent UI freezing
export const yieldToEventLoop = (): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, 0))
}

// Process batches with controlled concurrency and progressive callbacks
export const processBatchesConcurrent = async <T, R>(
    items: T[],
    batchSize: number,
    processFn: (batch: T[], batchIndex: number) => Promise<R>,
    concurrency: number = CONCURRENT_REQUESTS,
    onProgress?: ProgressCallback,
    onBatchStart?: BatchStartCallback,
    onBatchComplete?: BatchCompleteCallback<R>,
    signal?: AbortSignal
): Promise<R[]> => {
    const totalBatches = Math.ceil(items.length / batchSize)
    const results: R[] = new Array(totalBatches)
    let completedBatches = 0
    let processedTickers = 0

    // Create all batch definitions with start/end indices
    const batches: {
        batch: T[]
        index: number
        startIdx: number
        endIdx: number
    }[] = []
    for (let i = 0; i < items.length; i += batchSize) {
        const endIdx = Math.min(i + batchSize, items.length)
        batches.push({
            batch: items.slice(i, endIdx),
            index: Math.floor(i / batchSize),
            startIdx: i,
            endIdx: endIdx,
        })
    }

    // Process in chunks of `concurrency`
    for (let i = 0; i < batches.length; i += concurrency) {
        if (signal?.aborted) {
            throw new Error('Operation cancelled')
        }

        const chunk = batches.slice(i, i + concurrency)

        // Call onBatchStart for each batch in this chunk before processing
        if (onBatchStart) {
            for (const { index, startIdx, endIdx } of chunk) {
                await onBatchStart(index, startIdx, endIdx)
            }
        }

        const chunkResults = await Promise.all(
            chunk.map(async ({ batch, index, startIdx, endIdx }) => {
                const result = await processFn(batch, index)
                return {
                    result,
                    index,
                    batchSize: batch.length,
                    startIdx,
                    endIdx,
                }
            })
        )

        // Process results in order and call onBatchComplete for each
        for (const {
            result,
            index,
            batchSize: size,
            startIdx,
            endIdx,
        } of chunkResults) {
            // Check for cancellation before processing each result
            if (signal?.aborted) {
                throw new Error('Operation cancelled')
            }

            results[index] = result

            // Call the batch complete callback to allow progressive writing
            if (onBatchComplete) {
                await onBatchComplete(result, index, startIdx, endIdx)
            }

            // Check for cancellation after writing results
            if (signal?.aborted) {
                throw new Error('Operation cancelled')
            }

            completedBatches++
            processedTickers += size

            if (onProgress) {
                onProgress({
                    currentBatch: completedBatches,
                    totalBatches,
                    processedTickers,
                })
            }
        }

        // Yield to event loop between concurrent chunks
        await yieldToEventLoop()
    }

    return results
}
